// covered-by: frontends/carrie/src/components/ask-puntt-sources/__tests__/SourceHoverCard.test.tsx
import { ShieldCheck } from "@phosphor-icons/react";
import { Link, Text } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { type AskPunttSource } from "./types";

type SourcePreviewProps = {
  source: AskPunttSource;
  testIdPrefix: string;
  /** Icon or avatar rendered beside the title. */
  leading?: ReactNode;
};

export function SourceTitleLink({
  source,
  testIdPrefix,
}: {
  source: AskPunttSource;
  testIdPrefix: string;
}) {
  const linkTestId = `${testIdPrefix}-source-link-${source.id}`;
  const titleClass =
    "text-[14px] font-medium leading-5 text-puntt-neutral-gray-12";

  if (source.href != null && source.href !== "") {
    return (
      <Link
        href={source.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={linkTestId}
        className={`${titleClass} no-underline hover:underline`}
      >
        {source.title}
      </Link>
    );
  }

  return (
    <Text as="span" data-testid={linkTestId} className={titleClass}>
      {source.title}
    </Text>
  );
}

export function SourceTrustedFooter({
  source,
  testIdPrefix,
}: {
  source: AskPunttSource;
  testIdPrefix: string;
}) {
  const { t } = useTranslation("ui");

  if (
    source.kind !== "knowledge" ||
    source.addedBy == null ||
    source.addedBy === ""
  ) {
    return null;
  }

  return (
    <div
      className="flex w-full flex-col gap-2 border-t border-puntt-neutral-gray-6 p-4"
      data-testid={`${testIdPrefix}-source-trusted-${source.id}`}
    >
      <div className="flex items-center gap-2">
        <ShieldCheck
          size={16}
          aria-hidden="true"
          className="shrink-0 text-puntt-neutral-gray-11"
        />
        <Text
          as="span"
          size="3"
          weight="medium"
          className="text-puntt-neutral-gray-11"
        >
          {t("askPuntt.sources.trusted")}
        </Text>
      </div>
      <Text as="p" size="2" className="text-puntt-neutral-gray-12">
        {t("askPuntt.sources.addedBy", {
          name: source.addedBy,
          time: source.addedAtLabel ?? "",
        })}
      </Text>
    </div>
  );
}

export function SourcePreview({
  source,
  testIdPrefix,
  leading,
}: SourcePreviewProps) {
  const { t } = useTranslation("ui");
  const origin = [source.sourceName?.trim(), source.locationLabel?.trim()]
    .filter(Boolean)
    .join(" · ");
  const excerpt = source.excerpt?.trim();

  return (
    <>
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center gap-2">
          {leading}
          <SourceTitleLink source={source} testIdPrefix={testIdPrefix} />
        </div>
        {source.summary != null && source.summary !== "" ? (
          <Text
            as="p"
            size="2"
            data-testid={`${testIdPrefix}-source-summary-${source.id}`}
            className="line-clamp-3 text-[14px] leading-5 text-puntt-neutral-gray-12"
          >
            {source.summary}
          </Text>
        ) : null}
        {origin ? (
          <Text
            as="p"
            size="1"
            data-testid={`${testIdPrefix}-source-origin-${source.id}`}
            className="break-words text-puntt-neutral-gray-11"
          >
            {origin}
          </Text>
        ) : null}
        {excerpt ? (
          <div
            className="flex flex-col gap-1 rounded-md bg-puntt-neutral-gray-2 px-3 py-2"
            data-testid={`${testIdPrefix}-source-excerpt-${source.id}`}
          >
            {source.kind === "knowledge" ? (
              <Text as="p" size="1" className="text-puntt-neutral-gray-11">
                <em>{t("commentCitationLinks.fromKnowledgeDatabase")}</em>
              </Text>
            ) : null}
            <Text
              as="p"
              size="2"
              className="line-clamp-4 whitespace-pre-line break-words text-puntt-neutral-gray-12"
            >
              {excerpt}
            </Text>
          </div>
        ) : null}
      </div>
    </>
  );
}
